import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { LoaderIcon } from '@/components/icons';
import { Button } from '@/components/ui/button';
import { mcpClassApi } from '@/api/mcpClass.api';
import {
  emptyMCPClassForm,
  isMCPClassFormValid,
  toMCPClass,
  type MCPClassFormState,
} from '@/pages/mcp/mcp-class-form.utils';
import { MCPClassForm } from '@/pages/mcp/MCPClassForm';

export default function CreateMCPClassPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [form, setForm] = useState<MCPClassFormState>({ ...emptyMCPClassForm });

  const createMutation = useMutation({
    mutationFn: (f: MCPClassFormState) => mcpClassApi.createMCPClass(toMCPClass(f)),
    onSuccess: (cls) => {
      queryClient.invalidateQueries({ queryKey: ['mcp-classes'] });
      navigate(`/plugins/mcps/classes/${cls.id}`);
    },
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/plugins/mcps?tab=class')}
            className="h-8 w-8 p-0"
          >
            <svg
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
          </Button>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">新建 MCP 模版</h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              定义 MCP 模板与可配置参数，作为创建实例的蓝本。
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => navigate('/plugins/mcps?tab=class')}>
            取消
          </Button>
          <Button
            disabled={!isMCPClassFormValid(form) || createMutation.isPending}
            onClick={() => createMutation.mutate(form)}
          >
            {createMutation.isPending && <LoaderIcon className="mr-2 h-4 w-4" />}创建 MCP 模版
          </Button>
        </div>
      </div>

      <MCPClassForm form={form} onChange={setForm} />
    </div>
  );
}
